import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import React, { useEffect, useState } from "react";
import { InputText } from "primereact/inputtext";
import { ColorPicker } from "primereact/colorpicker";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";

export interface ICommonCalendarHeatmapProps {
  config: ICalendarHeatmapProps;
  onConfigChange: (newConfig: ICalendarHeatmapProps) => void;
}

export interface ICalendarHeatmapProps {
  identifier?: string;
  dataset?: HeatMapDataset[];
  isCustomDataset?: boolean;
  startDate: Date;
  setStartDate?: React.Dispatch<React.SetStateAction<Date>>;
  endDate: Date;
  setEndDate?: React.Dispatch<React.SetStateAction<Date>>;
  minValue?: number | undefined;
  maxValue?: number | undefined;
  lowValueTitle?: string | undefined;
  highValueTitle?: string | undefined;
  lowValueColor?: string;
  highValueColor?: string;
  showDateSelection?: boolean;
  staticMinMaxValues: boolean;
  staticMinMaxTitles: boolean;
  staticMinMaxColors: boolean;
  ignoreCurrentDay: boolean;
}

export interface HeatMapDataset {
  [x: string]: any;
  date: Date;
  value: number;
}

const getCurrentYearFirstDay = () => {
  const date = new Date();
  date.setMonth(0);
  date.setDate(1);
  return date;
};

const getCurrentYearLastDay = () => {
  const date = new Date();
  date.setMonth(11);
  date.setDate(31);
  return date;
};

const initialConfig: ICalendarHeatmapProps = {
  identifier: "",
  startDate: getCurrentYearFirstDay(),
  endDate: getCurrentYearLastDay(),
  minValue: undefined,
  maxValue: undefined,
  lowValueTitle: undefined,
  highValueTitle: undefined,
  lowValueColor: undefined,
  highValueColor: undefined,
  showDateSelection: true,
  staticMinMaxValues: false,
  staticMinMaxTitles: false,
  staticMinMaxColors: false,
  ignoreCurrentDay: false
};

const CalendarHeatmapEditor = ({ config, onConfigChange }: ICommonCalendarHeatmapProps) => {
  const [currentConfig, setCurrentConfig] = useState<ICalendarHeatmapProps>({
    ...initialConfig,
    ...config,
  });
  const [dateFrom, setDateFrom] = useState<Date>(new Date(currentConfig.startDate));
  const [dateTo, setDateTo] = useState<Date>(new Date(currentConfig.endDate));

  useEffect(() => {
    if (JSON.stringify(config) !== JSON.stringify(currentConfig)) {
      onConfigChange(currentConfig);
    }
  }, [currentConfig, onConfigChange, config]);

  const handleColorChange = (field: keyof ICalendarHeatmapProps, color: any) => {
    setCurrentConfig(prevConfig => ({
      ...prevConfig,
      [field]: color || "",
    }));
  };

  return (
    <div>
      <div className="flex flex-column gap-4">
        <div className="flex flex-row gap-3">
          <div className="calendar-container flex flex-column gap-1 w-3">
            <b style={{ padding: "0 0 0 5px" }}>From</b>
            <CalendarWrapper
              value={dateFrom}
              onChange={e => {
                setDateFrom(e);
                setCurrentConfig(prevConfig => ({ ...prevConfig, startDate: e }));
              }}
              dateFormat="yy-mm-dd"
            />
          </div>
          <div className="calendar-container flex flex-column gap-1 w-3">
            <b style={{ padding: "0 0 0 5px" }}>To</b>
            <CalendarWrapper
              value={dateTo}
              onChange={e => {
                setDateTo(e);
                setCurrentConfig(prevConfig => ({ ...prevConfig, endDate: e }));
              }}
              dateFormat="yy-mm-dd"
            />
          </div>
          <div className="flex flex-column gap-1">
            <label htmlFor="showDateSelection">Show date selection</label>
            <InputSwitch
              className="mt-auto mb-auto"
              checked={currentConfig.showDateSelection}
              onChange={e =>
                setCurrentConfig(prevConfig => ({ ...prevConfig, showDateSelection: e.value }))
              }
            />
          </div>
        </div>
        <div className="flex flex-column gap-1">
          <label htmlFor="identifier">Identifier</label>
          <DatasetDropdown
            value={currentConfig.identifier}
            onChange={e =>
              setCurrentConfig(prevConfig => ({ ...prevConfig, identifier: e as string }))
            }
          />
        </div>
        <div className="flex flex-row gap-3">
          <div className="flex flex-column gap-1">
            <label htmlFor="staticMinMaxValues">Min/Max Values</label>
            <InputSwitch
              checked={currentConfig.staticMinMaxValues}
              onChange={e => {
                const { value } = e;
                setCurrentConfig(prevConfig => ({
                  ...prevConfig,
                  staticMinMaxValues: value,
                  minValue: value ? prevConfig.minValue : undefined,
                  maxValue: value ? prevConfig.maxValue : undefined,
                }));
              }}
            />
          </div>
          <div className="flex flex-column gap-1">
            <label htmlFor="maxValue">Min/Max Titles</label>
            <InputSwitch
              checked={currentConfig.staticMinMaxTitles}
              onChange={e => {
                const { value } = e;
                setCurrentConfig(prevConfig => ({ ...prevConfig, staticMinMaxTitles: value }));
              }}
            />
          </div>
          <div className="flex flex-column gap-1">
            <label htmlFor="showDateSelection">Min/Max colors</label>
            <InputSwitch
              checked={currentConfig.staticMinMaxColors}
              onChange={e => {
                const { value } = e;
                setCurrentConfig(prevConfig => ({
                  ...prevConfig,
                  staticMinMaxColors: value,
                  lowValueColor: value ? prevConfig.lowValueColor : "#EF553B",
                  highValueColor: value ? prevConfig.highValueColor : "#636EFA",
                }));
              }}
            />
          </div>
          <div className="flex flex-column gap-1">
            <label htmlFor="showDateSelection">Ignore current day</label>
            <InputSwitch
              checked={currentConfig.ignoreCurrentDay}
              onChange={e => {
                const { value } = e;
                setCurrentConfig(prevConfig => ({
                  ...prevConfig,
                  ignoreCurrentDay: value
                }));
              }}
            />
          </div>
        </div>
        <div className="flex flex-row gap-3">
          {currentConfig.staticMinMaxValues && (
            <div className="flex flex-column gap-1">
              <label htmlFor="minValue">Min Value</label>
              <InputNumber
                value={currentConfig.minValue}
                onChange={e => {
                  const value = e.value || 0;
                  setCurrentConfig(prevConfig => ({ ...prevConfig, minValue: value }));
                }}
                placeholder="Min Value"
              />
            </div>
          )}
          {currentConfig.staticMinMaxTitles && (
            <div className="flex flex-column gap-1">
              <label htmlFor="">Min Value title</label>
              <InputText
                value={currentConfig.lowValueTitle}
                onChange={e => {
                  const { value } = e.target;
                  setCurrentConfig(prevConfig => ({ ...prevConfig, lowValueTitle: value }));
                }}
                placeholder="Low Value Title"
              />
            </div>
          )}
          {currentConfig.staticMinMaxColors && (
            <div className="flex flex-column gap-1">
              <label htmlFor="minValueColor">Min Value Color</label>
              <ColorPicker
                value={currentConfig.lowValueColor}
                onChange={e => handleColorChange("lowValueColor", e.value)}
              />
            </div>
          )}
        </div>
        <div className="flex flex-row gap-3">
          {currentConfig.staticMinMaxValues && (
            <div className="flex flex-column gap-1">
              <label htmlFor="maxValue">Max Value</label>
              <InputNumber
                value={currentConfig.maxValue}
                onChange={e => {
                  const value = e.value || 0;
                  setCurrentConfig(prevConfig => ({ ...prevConfig, maxValue: value }));
                }}
                placeholder="Max Value"
              />
            </div>
          )}
          {currentConfig.staticMinMaxTitles && (
            <div className="flex flex-column gap-1">
              <label htmlFor="">Max Value title</label>
              <InputText
                value={currentConfig.highValueTitle}
                onChange={e => {
                  const { value } = e.target;
                  setCurrentConfig(prevConfig => ({ ...prevConfig, highValueTitle: value }));
                }}
                placeholder="High Value Title"
              />
            </div>
          )}
          {currentConfig.staticMinMaxColors && (
            <div className="flex flex-column gap-1">
              <label htmlFor="maxValueColor">Max Value Color</label>
              <ColorPicker
                value={currentConfig.highValueColor}
                onChange={e => handleColorChange("highValueColor", e.value)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CalendarHeatmapEditor;
